import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface PricingSectionProps {
  onGetStarted: () => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ onGetStarted }) => {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const [billing, setBilling] = useState<'annual' | 'monthly'>('annual');

  const monthlyPrice = 29;
  const annualPrice = 19;
  const savingsPercent = Math.round((1 - annualPrice / monthlyPrice) * 100);

  return (
    <section id="pricing" className="py-20 bg-white">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            {t('landing.pricing.title')}
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8">
            {t('landing.pricing.subtitle')}
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setBilling('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                billing === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('landing.pricing.annual')}
              <span className="ml-1.5 px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-bold rounded-full">
                {t('landing.pricing.savePercent', { percentage: savingsPercent })}
              </span>
            </button>
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                billing === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('landing.pricing.monthly')}
            </button>
          </div>
        </div>

        <div
          className={`grid md:grid-cols-2 gap-8 max-w-3xl mx-auto transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Free */}
          <div className="p-8 rounded-2xl border-2 border-gray-200 bg-white">
            <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">{t('landing.pricing.freePlan.name')}</h3>
            <p className="text-4xl font-heading font-extrabold text-gray-900 mb-1">
              $0<span className="text-base font-normal text-gray-500">{t('landing.pricing.perMonth')}</span>
            </p>
            <p className="text-sm text-gray-500 mb-6">{t('landing.pricing.freePlan.tagline')}</p>

            <ul className="space-y-3 mb-8">
              {(t('landing.pricing.freePlan.features', { returnObjects: true }) as string[]).map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check size={16} className="text-success-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={onGetStarted}
              className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:border-primary hover:text-primary transition"
            >
              {t('common.getStarted')}
            </button>
          </div>

          {/* Pro */}
          <div className="relative p-8 rounded-2xl border-2 border-transparent bg-white shadow-xl"
            style={{ backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #7c3aed, #14b8a6)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box' }}
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-secondary text-white text-sm font-bold rounded-full flex items-center gap-1 shadow-lg">
              <Sparkles size={14} />
              {t('landing.pricing.proPlan.mostPopular')}
            </div>

            <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">{t('landing.pricing.proPlan.name')}</h3>
            <div className="mb-1">
              <span className="text-4xl font-heading font-extrabold text-gray-900">
                ${billing === 'annual' ? annualPrice : monthlyPrice}
              </span>
              <span className="text-base font-normal text-gray-500">{t('landing.pricing.perMonth')}</span>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              {billing === 'annual' ? (
                <>{t('landing.pricing.proPlan.billedAnnually', { amount: annualPrice * 12 })}</>
              ) : (
                <>{t('landing.pricing.proPlan.billedMonthly')}</>
              )}
            </p>

            <ul className="space-y-3 mb-8">
              {(t('landing.pricing.proPlan.features', { returnObjects: true }) as string[]).map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check size={16} className="text-secondary flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={onGetStarted}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-bold hover:opacity-90 transition shadow-md shadow-primary/20"
            >
              {t('common.startFreeTrial')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
